import { relativeTime } from '@/lib/format';
import type { LiveStatus, Vehicle } from '@/types/api';
import type { LivePosition } from '@/stores/live-store';

/**
 * The backend's DEVICE_OFFLINE_THRESHOLD_MINUTES. A vehicle silent for longer
 * than this is what the sweep job will flag, so the map should agree.
 *
 * "Silent" means no contact at all. A parked tracker sends heartbeats but no
 * positions, so the age of the last fix alone would grey out every parked
 * vehicle; `lastHeardAt` is the backend's record of any contact.
 */
export const OFFLINE_AFTER_MS = Number(import.meta.env.VITE_OFFLINE_MINUTES ?? 15) * 60_000;

/** Below this the vehicle is stationary as far as an operator is concerned. */
export const MOVING_ABOVE_KPH = 3;

const OFFLINE_MINUTES = Math.round(OFFLINE_AFTER_MS / 60_000);

/**
 * What each status count means, for the legends under the KPI tiles: a short
 * line that fits a tile, and the full rule for a hover or tap.
 */
export const STATUS_LEGEND: Record<LiveStatus, { short: string; full: string }> = {
  moving: {
    short: `Above ${MOVING_ABOVE_KPH} km/h`,
    full: `Latest position shows the vehicle travelling faster than ${MOVING_ABOVE_KPH} km/h.`,
  },
  idle: {
    short: 'Stopped, still reporting',
    full: `Stopped or crawling (${MOVING_ABOVE_KPH} km/h or less) and heard from in the last ${OFFLINE_MINUTES} minutes. Includes parked vehicles, engine on or off.`,
  },
  offline: {
    short: `No contact for ${OFFLINE_MINUTES}+ min`,
    full: `No position or check-in from the tracker for ${OFFLINE_MINUTES} minutes or more: no power, no signal, or no airtime.`,
  },
  alert: {
    short: 'Vehicles with open alerts',
    full: 'Vehicles with at least one open alert. They are counted here instead of under Moving, Idle or Offline.',
  },
};

export interface LiveVehicle {
  vehicle: Vehicle;
  status: LiveStatus;
  lat: number | null;
  lng: number | null;
  speedKph: number;
  headingDeg: number;
  ignition: boolean;
  /** ISO timestamp of the newest fix from either REST or the socket. */
  updatedAt: string | null;
  /** Newest contact of any kind: a fix or a heartbeat. A parked tracker only sends heartbeats. */
  heardAt: string | null;
  hasAlert: boolean;
}

/**
 * Merges the REST snapshot with whatever the socket has pushed since, then
 * derives the four-state status the UI is built around. `alert` deliberately
 * wins over motion: an operator needs to see the problem, not the speed.
 */
export function deriveLiveVehicle(
  vehicle: Vehicle,
  live: LivePosition | undefined,
  hasAlert: boolean,
  now = Date.now(),
): LiveVehicle {
  const restTime = vehicle.lastKnown?.recordedAt ? Date.parse(vehicle.lastKnown.recordedAt) : 0;
  const liveTime = live ? Date.parse(live.timestamp) : 0;
  const useLive = Boolean(live) && liveTime >= restTime;

  const coordinates = vehicle.lastKnown?.position?.coordinates;
  const lat = useLive ? live!.lat : (coordinates?.[1] ?? null);
  const lng = useLive ? live!.lng : (coordinates?.[0] ?? null);
  const speedKph = useLive ? live!.speedKph : (vehicle.lastKnown?.speedKph ?? 0);
  const headingDeg = useLive ? live!.headingDeg : (vehicle.lastKnown?.headingDeg ?? 0);
  const ignition = useLive ? live!.ignition : Boolean(vehicle.lastKnown?.ignition);
  const updatedAtMs = Math.max(restTime, liveTime);
  const updatedAt = updatedAtMs > 0 ? new Date(updatedAtMs).toISOString() : null;

  const heardAtMs = vehicle.lastHeardAt ? Date.parse(vehicle.lastHeardAt) : 0;
  const lastContactMs = Math.max(updatedAtMs, heardAtMs);
  const heardAt = lastContactMs > 0 ? new Date(lastContactMs).toISOString() : null;
  const stale = lastContactMs === 0 || now - lastContactMs > OFFLINE_AFTER_MS;

  let status: LiveStatus;
  if (hasAlert) status = 'alert';
  else if (stale) status = 'offline';
  else if (speedKph > MOVING_ABOVE_KPH) status = 'moving';
  else status = 'idle';

  return { vehicle, status, lat, lng, speedKph, headingDeg, ignition, updatedAt, heardAt, hasAlert };
}

export const STATUS_ORDER: Record<LiveStatus, number> = {
  alert: 0,
  moving: 1,
  idle: 2,
  offline: 3,
};

export const STATUS_LABEL: Record<LiveStatus, string> = {
  moving: 'Moving',
  idle: 'Idle',
  offline: 'Offline',
  alert: 'Alert',
};

/** Hex fallbacks for canvas/WebGL, which cannot read a CSS custom property. */
export const STATUS_HEX: Record<LiveStatus, { light: string; dark: string }> = {
  moving: { light: '#1aa563', dark: '#35d98c' },
  idle: { light: '#e0980f', dark: '#f7bb3d' },
  offline: { light: '#8189a0', dark: '#9aa2b6' },
  alert: { light: '#e8433f', dark: '#ff6a63' },
};

/** A check-in this much newer than the last fix is worth showing separately. */
const SEPARATE_CHECK_IN_MS = 60_000;

/**
 * "last position 1h ago · checked in 2 min ago". A parked tracker keeps
 * checking in without sending positions, so one "updated" time would read as
 * if the tracker had gone quiet when only the vehicle had stopped.
 */
export function contactSummary(entry: Pick<LiveVehicle, 'updatedAt' | 'heardAt'>, capitalise = false): string {
  const fixMs = entry.updatedAt ? Date.parse(entry.updatedAt) : 0;
  const heardMs = entry.heardAt ? Date.parse(entry.heardAt) : 0;
  let text: string;
  if (!fixMs && !heardMs) text = 'no data yet';
  else if (!fixMs) text = `no position yet · checked in ${relativeTime(entry.heardAt)}`;
  else if (heardMs - fixMs > SEPARATE_CHECK_IN_MS) {
    text = `last position ${relativeTime(entry.updatedAt)} · checked in ${relativeTime(entry.heardAt)}`;
  } else text = `last position ${relativeTime(entry.updatedAt)}`;
  return capitalise ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}
